import { ReportViewerState } from '@mode/reports/sandbox/contract';
import { DataViewTypes } from '@mode/shared/contract-common';
import { createSelector } from 'reselect';

export const legacyEmbedDatasets = (state: ReportViewerState) =>
  state.reportFeature.serializedReport.legacyEmbedDatasets;

export const legacyEmbedDatasetsForLocalQueriesOrderedByCreationDate = createSelector(
  (state: ReportViewerState) => state.reportFeature.serializedReport.localQueryTokensSortedByCreationDate,
  legacyEmbedDatasets,
  (tokens, data) => tokens.map((t) => data[t])
);

export const legacyEmbedDatasetsLoadedForAllRunQueries = createSelector(
  (state: ReportViewerState) => state.reportFeature.serializedReport.queryRunStatus,
  legacyEmbedDatasets,
  (status, datasets) => {
    const tokensForQueriesThatHaveRun = Object.entries(status)
      .filter(([_, status]) => status.state !== DataViewTypes.QueryRunStatus.NotYetRun)
      .map(([token]) => token);

    return (
      tokensForQueriesThatHaveRun.length > 0 &&
      tokensForQueriesThatHaveRun.every((token) => status[token].datasetLoaded && token in datasets)
    );
  }
);

// Using 'Dataset' generically here (Queries, imported Datasets, etc)
const datasetRunStatuses = createSelector(
  (state: ReportViewerState) => state.reportFeature.serializedReport.queryRunStatus,
  (queryRunStatus) => queryRunStatus
);

const localQueryTokensSortedByCreationDate = createSelector(
  (state: ReportViewerState) => state.reportFeature.serializedReport.localQueryTokensSortedByCreationDate,
  (tokens) => tokens
);

const datasetTokensForAllRunDatasets = createSelector(datasetRunStatuses, (statuses) =>
  Object.entries(statuses)
    .filter(([_, status]) => status.state !== DataViewTypes.QueryRunStatus.NotYetRun)
    .map(([token]) => token)
);

export const hasImportedDatasets = createSelector(
  localQueryTokensSortedByCreationDate,
  datasetTokensForAllRunDatasets,
  (localQueryTokens, allTokens) => !allTokens.every((token) => localQueryTokens.includes(token))
);

export const legacyEmbedDatasetsLoadedForAllLocalQueries = createSelector(
  localQueryTokensSortedByCreationDate,
  datasetRunStatuses,
  legacyEmbedDatasets,
  (expectedLegacyEmbedDatasets, statuses, legacyEmbedDatasets) => {
    const hasQueryRunStatuses = Object.keys(statuses).length > 0;
    return (
      // We should have at least one query status
      hasQueryRunStatuses &&
      // For every dataset we expect to be present
      expectedLegacyEmbedDatasets.every(
        (token) =>
          // There should be a query status
          token in statuses &&
          // the status should be loaded or NotYetRun
          (statuses[token].datasetLoaded || statuses[token].state === DataViewTypes.QueryRunStatus.NotYetRun) &&
          // we should have the expected dataset
          token in legacyEmbedDatasets
      )
    );
  }
);

export const datasetResultsLoadedForAllRunDatasets = createSelector(
  (state: ReportViewerState) => state.reportFeature.serializedReport.queryRunStatus,
  (state: ReportViewerState) => state.reportFeature.serializedReport.datasetResults,
  (status, datasets) => {
    const tokensForQueriesThatHaveRun = Object.entries(status)
      .filter(([_, status]) => status.state !== DataViewTypes.QueryRunStatus.NotYetRun)
      .map(([token]) => token);

    const allDatasetTokens = datasets.map((dataset) => dataset.queryToken);

    return (
      tokensForQueriesThatHaveRun.length > 0 &&
      tokensForQueriesThatHaveRun.every((token) => status[token].datasetLoaded && allDatasetTokens.includes(token))
    );
  }
);
